import TextButton from '@oberoninternal/travelbase-ds/components/action/TextButton';
import Modal from '@oberoninternal/travelbase-ds/components/layout/Modal';
import ModalHeader from '@oberoninternal/travelbase-ds/components/layout/ModalHeader';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import { Maybe } from '@oberoninternal/travelbase-ds/entities/Maybe';
import useSesame from '@oberoninternal/travelbase-ds/hooks/useSesame';
import { Box, Flex } from '@rebass/grid';
import { gql } from '@apollo/client';
import isBefore from 'date-fns/isBefore';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { useTenantContext } from '../context/TenantContext';
import { Locale } from '../entities/Locale';
import { OrderItem_Booking_Fragment, OrderItemFragment } from '../generated/graphql';
import createImgProps from '../utils/createImgProps';
import createUnitTitle from '../utils/createUnitTitle';
import downloadTorFile from '../utils/downloadTORFile';
import { declinedStatuses } from '../utils/order';
import { getTripDuration } from '../utils/trip';
import Anchor from './designsystem/Anchor';
import OrderItem, {
    OrderItemDisclaimer,
    OrderItemInfo,
    OrderItemName,
    OrderItemProps,
    OrderItemTextButton,
    OrderItemThumbnail,
} from './designsystem/OrderItem';
import ReviewModal from './ReviewModal';
import Stack from './Stack';
import Download from './svg/Download';
import RatingStar from './svg/RatingStar';
import { useDeviceSize } from '@oberoninternal/travelbase-ds/context/devicesize';
import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import AsyncButton from './AsyncButton';

export const fragment = gql`
    fragment OrderBooking on Booking {
        id
        downloadVoucherUrl
        isCancellable
        isAlterable
        isTravelGroupAlterable
        isSurchargesAlterable
        customerComment
        ...ReviewModal
    }
`;

interface OrderDetailsBookingItemProps extends OrderItemProps {
    onLeaveReviewClick?: () => void;
    orderId?: Maybe<string>;
    data: OrderItem_Booking_Fragment;
    renderAccommodationLink?: boolean;
}

const getDisclaimerText = (orderItem: OrderItem_Booking_Fragment) => {
    switch (orderItem.status) {
        case 'ACCEPTED': {
            if (orderItem.downloadVoucherUrl) {
                return (
                    <FormattedMessage defaultMessage="Je voucher (betalingsbewijs en sleuteladres) is nu beschikbaar" />
                );
            }
            return <FormattedMessage defaultMessage="Je kunt je voucher downloaden zodra het restant is betaald" />;
        }
        case 'PENDING':
            return <FormattedMessage defaultMessage="Deze reserveringsaanvraag is in behandeling" />;
        case 'DECLINED':
            return '';
        case 'CANCELLED_PARTNER':
        case 'CANCELLED_EXTERNAL':
        case 'CANCELLED_GUEST':
            return <FormattedMessage defaultMessage="Geannuleerde boeking" />;
        default:
            return '';
    }
};

export const orderItemIsBooking = (item: OrderItemFragment): item is OrderItem_Booking_Fragment =>
    item.__typename === 'Booking';

const OrderBooking = ({ data, onLeaveReviewClick, orderId, ...props }: OrderDetailsBookingItemProps) => {
    const { open, onClose, onOpen } = useSesame();

    const {
        rentalUnit,
        arrivalDate,
        departureDate,
        review,
        status,
        downloadVoucherUrl,
        isCancellable,
        isAlterable,
        customerComment,
        id,
    } = data;
    const readReviewModal = useSesame();
    const router = useRouter();
    const cancellableModal = useSesame();
    const { brandConfig } = useTenantContext();
    const device = useDeviceSize();

    if (!rentalUnit) {
        return null;
    }

    const isDeclined =
        status === 'CANCELLED_PARTNER' || status === 'CANCELLED_EXTERNAL' || status === 'CANCELLED_GUEST';
    const {
        name,
        accommodation: { hasPublicPage, name: accommodationName },
    } = rentalUnit;

    const bookingIsDone = isBefore(new Date(departureDate), new Date());
    const itemName = createUnitTitle(name, accommodationName, hasPublicPage);

    const { linkProps, renderAccommodationLink } = props;
    return (
        <OrderItem {...props}>
            <Flex alignItems={['left', 'center']} flex={1} flexDirection={['column', null, null, 'row']}>
                <Stack>
                    {rentalUnit?.jpegThumbnail && (
                        <OrderItemThumbnail
                            faded={declinedStatuses.includes(status)}
                            {...createImgProps(rentalUnit.jpegThumbnail, rentalUnit.webpThumbnail)}
                        />
                    )}
                </Stack>
                <Stack
                    flex="1"
                    flexWrap="wrap"
                    my={[0, 4, null, null, null, null, 0]}
                    ml={[0, null, 4, null, null, null, 0]}
                    mx={2}
                >
                    <Stack p={[4, null, null, 0]} width="100%" variant="inline">
                        {!bookingIsDone && <OrderItemDisclaimer>{getDisclaimerText(data)}</OrderItemDisclaimer>}
                    </Stack>
                    <Stack variant="inline" flexWrap="wrap">
                        <OrderItemInfo>
                            <Stack>
                                <OrderItemName>
                                    {linkProps?.href && renderAccommodationLink ? (
                                        <Link passHref {...linkProps} target="_blank" rel="noreferrer" legacyBehavior>
                                            {itemName}
                                        </Link>
                                    ) : (
                                        itemName
                                    )}
                                </OrderItemName>
                            </Stack>
                            <Body variant="small">
                                <FormattedMessage
                                    defaultMessage="Accommodatie, {date} — {duration}"
                                    values={{
                                        date: <FormattedDate format="DAYMONTHYEAR" value={new Date(arrivalDate)} />,
                                        duration: (
                                            <FormattedMessage
                                                defaultMessage="{duration, number} {duration, plural, one {nacht} other {nachten}}"
                                                values={{
                                                    duration: getTripDuration(arrivalDate, departureDate),
                                                }}
                                            />
                                        ),
                                    }}
                                />
                            </Body>
                            <Flex flexDirection="row">
                                <Body variant="small">
                                    {customerComment && (
                                        <TextButton size="tiny" onClick={onOpen}>
                                            <FormattedMessage defaultMessage="Lees opmerking" />
                                        </TextButton>
                                    )}
                                </Body>
                            </Flex>

                            <Stack
                                mt={4}
                                flex={device === 'mobile' || device === 'tablet' ? 1 : 'unset'}
                                variant={device === 'mobile' || device === 'tablet' ? 'default' : 'inline'}
                                spacing={4}
                                style={{ zIndex: 1 }}
                            >
                                {!review && bookingIsDone && onLeaveReviewClick && (
                                    <AsyncButton onClick={onLeaveReviewClick}>
                                        <RatingStar />
                                        <span>
                                            <FormattedMessage defaultMessage="Beoordelen" />
                                        </span>
                                    </AsyncButton>
                                )}
                                {!bookingIsDone && (
                                    <>
                                        {downloadVoucherUrl && (
                                            <AsyncButton onClick={() => downloadTorFile(downloadVoucherUrl, itemName)}>
                                                <Download />
                                                <span>
                                                    <FormattedMessage defaultMessage="Voucher" />
                                                </span>
                                            </AsyncButton>
                                        )}
                                        {isAlterable && (
                                            <Link
                                                href={{
                                                    pathname: `/my/order/${orderId}/booking/${id}/alter`,
                                                    query: { orderId },
                                                }}
                                                passHref
                                                legacyBehavior
                                            >
                                                <Button as="div" variant="outline">
                                                    <FormattedMessage defaultMessage="Wijzigen" />
                                                </Button>
                                            </Link>
                                        )}
                                        {!isCancellable && !isDeclined && (
                                            <>
                                                <Button onClick={cancellableModal.onOpen} variant="outline">
                                                    <FormattedMessage defaultMessage="Annuleren" />
                                                </Button>
                                                <Modal
                                                    onClose={cancellableModal.onClose}
                                                    open={cancellableModal.open}
                                                    variant="small"
                                                >
                                                    <ModalHeader variant="small">
                                                        <FormattedMessage defaultMessage="Boeking annuleren" />
                                                    </ModalHeader>
                                                    <Box my={5}>
                                                        <FormattedMessage defaultMessage="Neem contact met ons op om deze boeking te annuleren." />
                                                        <br />
                                                        <Anchor
                                                            as="a"
                                                            target="_blank"
                                                            rel="noreferrer noopener"
                                                            href={brandConfig.contact.page?.[router.locale as Locale]}
                                                        >
                                                            <FormattedMessage defaultMessage="Neem contact op" />
                                                        </Anchor>
                                                    </Box>
                                                </Modal>
                                            </>
                                        )}
                                        {isCancellable && (
                                            <Link
                                                href={`/my/order/${orderId}/booking/${id}/cancel`}
                                                passHref
                                                legacyBehavior
                                            >
                                                <Button as="div" variant="outline">
                                                    <FormattedMessage defaultMessage="Annuleren" />
                                                </Button>
                                            </Link>
                                        )}
                                    </>
                                )}
                                {bookingIsDone && review && (
                                    <>
                                        <OrderItemTextButton
                                            onClick={readReviewModal.onOpen}
                                            as={TextButton}
                                            size="tiny"
                                        >
                                            <FormattedMessage defaultMessage="Beoordeling lezen" />
                                        </OrderItemTextButton>
                                        <ReviewModal
                                            data={data}
                                            open={readReviewModal.open}
                                            onClose={readReviewModal.onClose}
                                        />
                                    </>
                                )}
                            </Stack>
                        </OrderItemInfo>
                    </Stack>
                </Stack>
            </Flex>

            <Modal variant="regular" open={open} onClose={onClose}>
                <ModalHeader>
                    <FormattedMessage defaultMessage="Opmerking" />
                </ModalHeader>
                <Stack mt={4} spacing={4}>
                    <Body>{customerComment}</Body>
                </Stack>
            </Modal>
        </OrderItem>
    );
};

export default OrderBooking;
